import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import {
  UnitSystem,
  defaultSystemForLocale,
  tempUnit,
  weightUnit,
  tempToDisplay,
  tempToCanonical,
  weightToDisplay,
  weightToCanonical,
  formatTemp,
  formatWeight,
  heightUnit,
  cmToFtIn,
  ftInToCm,
  formatHeight,
} from "@/lib/units";

const STORAGE_KEY = "tl-units";

interface UnitsContextValue {
  /** Active unit system (explicit user choice, else locale default). */
  system: UnitSystem;
  /** True when the user explicitly picked a system (overrides locale). */
  isExplicit: boolean;
  setSystem: (s: UnitSystem) => void;
  tempUnit: string;
  weightUnit: string;
  tempToDisplay: (c: number | null | undefined) => number | null;
  tempToCanonical: (v: number | null | undefined) => number | null;
  weightToDisplay: (kg: number | null | undefined) => number | null;
  weightToCanonical: (v: number | null | undefined) => number | null;
  formatTemp: (c: number | null | undefined, decimals?: number) => string;
  formatWeight: (kg: number | null | undefined, decimals?: number) => string;
  heightUnit: string;
  cmToFtIn: (cm: number | null | undefined) => { ft: number; in: number } | null;
  ftInToCm: (ft: number | null | undefined, inches: number | null | undefined) => number | null;
  formatHeight: (cm: number | null | undefined) => string;
}

function readStored(): UnitSystem | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "us" || v === "metric" ? v : null;
  } catch {
    return null;
  }
}

const UnitsContext = createContext<UnitsContextValue | null>(null);

export function UnitsProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<UnitSystem | null>(() => readStored());
  const [localeDefault] = useState<UnitSystem>(() => defaultSystemForLocale());

  const system = stored ?? localeDefault;

  useEffect(() => {
    if (!stored) return;
    try {
      localStorage.setItem(STORAGE_KEY, stored);
    } catch {
      /* ignore */
    }
  }, [stored]);

  const value = useMemo<UnitsContextValue>(
    () => ({
      system,
      isExplicit: stored != null,
      setSystem: setStored,
      tempUnit: tempUnit(system),
      weightUnit: weightUnit(system),
      tempToDisplay: (c) => tempToDisplay(c, system),
      tempToCanonical: (v) => tempToCanonical(v, system),
      weightToDisplay: (kg) => weightToDisplay(kg, system),
      weightToCanonical: (v) => weightToCanonical(v, system),
      formatTemp: (c, d) => formatTemp(c, system, d),
      formatWeight: (kg, d) => formatWeight(kg, system, d),
      heightUnit: heightUnit(system),
      cmToFtIn,
      ftInToCm,
      formatHeight: (cm) => formatHeight(cm, system),
    }),
    [system, stored]
  );

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
}

export function useUnits(): UnitsContextValue {
  const ctx = useContext(UnitsContext);
  if (ctx) return ctx;
  // Fallback for components rendered outside the provider (e.g. tests).
  const system = defaultSystemForLocale();
  return {
    system,
    isExplicit: false,
    setSystem: () => {},
    tempUnit: tempUnit(system),
    weightUnit: weightUnit(system),
    tempToDisplay: (c) => tempToDisplay(c, system),
    tempToCanonical: (v) => tempToCanonical(v, system),
    weightToDisplay: (kg) => weightToDisplay(kg, system),
    weightToCanonical: (v) => weightToCanonical(v, system),
    formatTemp: (c, d) => formatTemp(c, system, d),
    formatWeight: (kg, d) => formatWeight(kg, system, d),
    heightUnit: heightUnit(system),
    cmToFtIn,
    ftInToCm,
    formatHeight: (cm) => formatHeight(cm, system),
  };
}
